import React, {
    useState,
    useEffect,
    useMemo,
    useCallback,
    useRef,
} from 'react';
import { MdCleaningServices, MdSettings, MdSync } from 'react-icons/md';
import { injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import axios from 'axios';

import { Button, Tooltip, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';

import CardSideBordered from '~/components/CardSideBordered';
import InfoCard from '~/components/InfoCard';
import LocaleMessage from '~/components/LocaleMessage';
import PageContent from '~/components/PageContent';
import ExportCSVButton from '~/components/Reports/CSVButton';
import ReportHeader from '~/components/Reports/ReportHeader';

import history from '~/services/history';
import api from '~/services/pluginbot-api';
import getDateLocale from '~/util/GetDateLocale';
import GetFileName from '~/util/GetFileName';
import GetPluginspaceTheme from '~/util/PluginspaceTheme';

import { Container } from '../../Dashboard/styles';
import CleaningReportAreaByHourCard from './CleaningReportAreaByHourCard';
import CleaningReportAreaComparisonCard from './CleaningReportAreaComparisonCard';
import CleaningReportBatteryByModeCard from './CleaningReportBatteryByModeCard';
import CleaningReportBatteryDashboard from './CleaningReportBatteryDashboard';
import CleaningReportChartsCard from './CleaningReportChartsCard';
import { GENERIC_DETAIL_ERROR } from './cleaningReportDetailConstants';
import CleaningReportDetailModal from './CleaningReportDetailModal';
// import CleaningReportFleetScheduleCard from './CleaningReportFleetScheduleCard';
import CleaningReportProductSpentCard from './CleaningReportProductSpentCard';
import CleaningReportROICard from './CleaningReportROICard';
import { buildCleaningReportShortcutList } from './cleaningReportShortcutList';
import CleaningReportTasksTablePagination, {
    CLEANING_REPORT_TASKS_TABLE_DEFAULT_ROWS_PER_PAGE,
} from './CleaningReportTasksTablePagination';
import CleaningReportUtilityCharts from './CleaningReportUtilityCharts';
import CleaningReportUtilityInfoCards from './CleaningReportUtilityInfoCards';
import {
    buildCleanAreaByHourOfDay,
    buildCleaningReportCsvData,
    buildCleanAreaVsTaskAreaByDay,
    buildCleaningReportApiTimeRange,
    cleaningDetailErrorMessageFromPayload,
    cleaningReportDefaultShortcut,
    defaultCleaningReportDateRange,
    formatCleaningDuration,
    formatCleaningDurationNoSeconds,
    formatCleaningReportCreateDateTime,
    getCleaningReportTaskStartTime,
    getCleaningModeLabel,
    getCleaningReportChartColors,
    getCleaningReportCsvHeaders,
    getCleaningTaskStatusLabel,
    localeToBcp47,
    normalizeCleaningReportResponse,
    scrollCleaningReportTableIntoView,
    summarizeList,
} from './cleaningReportUtils';
import {
    useCleaningReportCurrShortcutSync,
    useCleaningReportPeriodHandlers,
} from './useCleaningReportPeriod';

const RATE_LIMITED_DETAIL_ERROR = 'RATE_LIMITED';

function CleaningReportsDashboard({ match, intl }) {
    const { id } = match.params;
    const theme = useTheme();
    const colors = GetPluginspaceTheme(theme);

    const [robot, setRobot] = useState(null);
    const [cleaningApp, setCleaningApp] = useState(null);
    const cleaningAppSettings =
        cleaningApp && cleaningApp.settings
            ? cleaningApp.settings.settings || cleaningApp.settings
            : {};
    const [reportLoading, setReportLoading] = useState(true);
    const [initialDone, setInitialDone] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [reportPayload, setReportPayload] = useState(() =>
        normalizeCleaningReportResponse(null)
    );
    const [periodDates, setPeriodDates] = useState(
        defaultCleaningReportDateRange
    );
    const [draftPeriod, setDraftPeriod] = useState(
        defaultCleaningReportDateRange
    );
    const [currShortcut, setCurrShortcut] = useState(() => {
        const d = defaultCleaningReportDateRange();
        return cleaningReportDefaultShortcut(d.startDate, d.endDate);
    });

    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);
    const [detailData, setDetailData] = useState(null);

    const [tasksTablePage, setTasksTablePage] = useState(0);
    const [tasksTableRowsPerPage, setTasksTableRowsPerPage] = useState(
        CLEANING_REPORT_TASKS_TABLE_DEFAULT_ROWS_PER_PAGE
    );

    const cleaningTableRef = useRef(null);
    const [pollResetKey, setPollResetKey] = useState(0);
    const fetchGenRef = useRef(0);
    const cancelSourceRef = useRef(null);
    const inFlightRef = useRef(false);

    const reduxSettings = useSelector(state => state.settings || {});
    const localeCode =
        (reduxSettings.locale && reduxSettings.locale.code) || 'pt_BR';
    const date_loc = getDateLocale(reduxSettings);

    const shortcut_list = buildCleaningReportShortcutList();

    const { list, total, consumption } = reportPayload;

    const robotDilutions = useMemo(() => {
        if (!robot || !robot.id) return [];
        const raw = cleaningAppSettings.chemical_dilution_ratio;
        const dilution = raw === '' || raw == null ? 10 : parseFloat(raw);
        if (!Number.isFinite(dilution) || dilution < 0) return [];
        let snFromList = '';
        list.forEach(item => {
            if (snFromList) return;
            const sn = item && (item.sn || item.device_sn);
            if (sn) snFromList = sn;
        });
        return [
            {
                robot_id: robot.id,
                sn: String(robot.code || snFromList || ''),
                dilution_ml_per_l: dilution,
            },
        ];
    }, [robot, cleaningAppSettings.chemical_dilution_ratio, list]);

    const csvHeaders = useMemo(() => {
        return getCleaningReportCsvHeaders(intl, false);
    }, [intl]);

    const csvData = useMemo(() => {
        return buildCleaningReportCsvData(list, localeCode);
    }, [list, localeCode]);

    const areaCompareSeries = useMemo(
        () => buildCleanAreaVsTaskAreaByDay(list, localeToBcp47(localeCode)),
        [list, localeCode]
    );
    const areaByHourSeries = useMemo(() => buildCleanAreaByHourOfDay(list), [
        list,
    ]);
    const summary = useMemo(() => summarizeList(list), [list]);
    const totalCleanFormatted = formatCleaningDurationNoSeconds(
        summary.totalCleanTimeSec
    );
    const totalArea = summary.totalCleanArea.toFixed(1);

    const tasksTableRows = useMemo(() => {
        const start = tasksTablePage * tasksTableRowsPerPage;
        return list.slice(start, start + tasksTableRowsPerPage);
    }, [list, tasksTablePage, tasksTableRowsPerPage]);

    useEffect(() => {
        setTasksTablePage(0);
    }, [periodDates.startDate, periodDates.endDate, id]);

    useEffect(() => {
        const totalPages = Math.max(
            1,
            Math.ceil(list.length / tasksTableRowsPerPage)
        );
        const maxPage = totalPages - 1;
        if (tasksTablePage > maxPage) {
            setTasksTablePage(Math.max(0, maxPage));
        }
    }, [list.length, tasksTableRowsPerPage, tasksTablePage]);

    const fetchCleaningReportForRange = useCallback(
        (startYmd, endYmd, options = {}) => {
            const { silent = false, forceRefresh = false } = options;
            const { start_time, end_time } = buildCleaningReportApiTimeRange(
                startYmd,
                endYmd
            );
            if (silent && inFlightRef.current) {
                return Promise.resolve();
            }
            if (cancelSourceRef.current) {
                cancelSourceRef.current.cancel();
            }
            const source = axios.CancelToken.source();
            cancelSourceRef.current = source;
            const gen = fetchGenRef.current + 1;
            fetchGenRef.current = gen;
            inFlightRef.current = true;
            if (!silent) {
                setReportLoading(true);
            }
            setLoadError(null);
            return api
                .get(`robots/${id}/cleaning-report`, {
                    params: {
                        start_time,
                        end_time,
                        timezone_offset: -new Date().getTimezoneOffset() / 60,
                        ...(forceRefresh ? { refresh: 'true' } : {}),
                    },
                    cancelToken: source.token,
                })
                .then(res => {
                    if (gen !== fetchGenRef.current) return;
                    setReportPayload(normalizeCleaningReportResponse(res.data));
                })
                .catch(err => {
                    if (axios.isCancel(err) || gen !== fetchGenRef.current) {
                        return;
                    }
                    const { response } = err;
                    setReportPayload(
                        normalizeCleaningReportResponse(
                            response ? response.data : undefined
                        )
                    );
                    setLoadError(err);
                })
                .finally(() => {
                    if (gen !== fetchGenRef.current) return;
                    inFlightRef.current = false;
                    if (!silent) {
                        setReportLoading(false);
                    }
                    setInitialDone(true);
                });
        },
        [id]
    );

    const fetchCleaningReport = useCallback(
        (silent = false) => {
            return fetchCleaningReportForRange(
                periodDates.startDate,
                periodDates.endDate,
                { silent }
            );
        },
        [
            fetchCleaningReportForRange,
            periodDates.startDate,
            periodDates.endDate,
        ]
    );

    useEffect(() => {
        let cancelled = false;
        api.get(`robots/${id}`)
            .then(res => {
                if (cancelled) return;
                const robotData = res.data;
                setRobot(robotData);

                const cleaningAppId =
                    robotData &&
                    robotData.settings &&
                    robotData.settings.applications &&
                    robotData.settings.applications.cleaning;

                if (cleaningAppId) {
                    api.get(`applications/${cleaningAppId}`)
                        .then(appRes => {
                            if (!cancelled) setCleaningApp(appRes.data);
                        })
                        .catch(() => {
                            if (!cancelled) setCleaningApp(null);
                        });
                } else {
                    // Fallback to searching by robot ID if not in settings
                    api.get(`applications`, { params: { robot: id } })
                        .then(appRes => {
                            if (cancelled) return;
                            const apps = Array.isArray(appRes.data)
                                ? appRes.data
                                : [];
                            const cApp = apps.find(
                                a =>
                                    a.app_type && a.app_type.slug === 'cleaning'
                            );
                            setCleaningApp(cApp);
                        })
                        .catch(() => {
                            if (!cancelled) setCleaningApp(null);
                        });
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setRobot(null);
                    setCleaningApp(null);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [id]);

    const resetPollInterval = useCallback(() => {
        setPollResetKey(key => key + 1);
    }, []);

    useEffect(() => {
        fetchCleaningReport(false);
    }, [fetchCleaningReport]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            fetchCleaningReport(true);
        }, 60 * 1000);
        return () => clearInterval(intervalId);
    }, [fetchCleaningReport, pollResetKey]);

    useCleaningReportCurrShortcutSync(draftPeriod, setCurrShortcut);

    const goToRobotSettings = () => {
        history.push(`/robots/${id}`);
    };

    const scrollToCleaningTable = useCallback(
        () => scrollCleaningReportTableIntoView(cleaningTableRef),
        []
    );

    const fetchCleaningReportForRangeWithPollReset = useCallback(
        (startYmd, endYmd) => {
            resetPollInterval();
            return fetchCleaningReportForRange(startYmd, endYmd);
        },
        [fetchCleaningReportForRange, resetPollInterval]
    );

    const fetchCleaningReportForceFull = useCallback(() => {
        resetPollInterval();
        return fetchCleaningReportForRange(
            periodDates.startDate,
            periodDates.endDate,
            { forceRefresh: true }
        );
    }, [
        fetchCleaningReportForRange,
        periodDates.startDate,
        periodDates.endDate,
        resetPollInterval,
    ]);

    const {
        applyPeriod,
        handleCleaningFromDateChange,
        handleCleaningUntilDateChange,
        handleCleaningPeriodShortcut,
    } = useCleaningReportPeriodHandlers(
        draftPeriod,
        setDraftPeriod,
        periodDates,
        setPeriodDates,
        fetchCleaningReportForRangeWithPollReset
    );

    const closeCleaningDetail = useCallback(() => {
        setDetailModalOpen(false);
        setDetailLoading(false);
        setDetailError(null);
        setDetailData(null);
    }, []);

    const handleTasksTablePageChange = useCallback((event, newPage) => {
        setTasksTablePage(newPage);
    }, []);

    const handleTasksTableRowsPerPageChange = useCallback(event => {
        setTasksTableRowsPerPage(parseInt(event.target.value, 10));
        setTasksTablePage(0);
    }, []);

    const openCleaningDetail = useCallback(
        row => {
            if (
                (row && row.report_id) == null ||
                String(row.report_id).trim() === ''
            ) {
                return;
            }
            setDetailModalOpen(true);
            setDetailError(null);
            if (
                row.details &&
                typeof row.details === 'object' &&
                Object.keys(row.details).length > 0
            ) {
                setDetailData({ ...row, ...row.details });
                setDetailLoading(false);
                return;
            }
            setDetailLoading(true);
            setDetailData(null);
            const { start_time, end_time } = buildCleaningReportApiTimeRange(
                periodDates.startDate,
                periodDates.endDate
            );
            const rid = encodeURIComponent(String(row.report_id).trim());
            api.get(`robots/${id}/cleaning-report/detail/${rid}`, {
                params: { start_time, end_time },
            })
                .then(({ data }) => {
                    if (
                        data &&
                        typeof data === 'object' &&
                        typeof data.code === 'string' &&
                        typeof data.error === 'string'
                    ) {
                        setDetailError(
                            data.code === 'rate_limited' || data.retryable
                                ? RATE_LIMITED_DETAIL_ERROR
                                : data.error || GENERIC_DETAIL_ERROR
                        );
                        setDetailData(null);
                    } else {
                        setDetailData(data);
                        setDetailError(null);
                    }
                })
                .catch(err => {
                    const { response } = err;
                    const apiMsg = cleaningDetailErrorMessageFromPayload(
                        response && response.data
                    );
                    setDetailError(apiMsg || GENERIC_DETAIL_ERROR);
                    setDetailData(null);
                })
                .finally(() => {
                    setDetailLoading(false);
                });
        },
        [id, periodDates.startDate, periodDates.endDate]
    );

    const {
        graphColor,
        cardColor,
        pieSegmentColors,
    } = getCleaningReportChartColors(colors);

    return (
        <PageContent
            title={<LocaleMessage msg="page.robots.cleaning_report.title" />}
            loading={!initialDone || reportLoading}
        >
            <Container $fullWidth>
                <>
                    <div className="row mb-4 align-items-center">
                        <div className="col-md-8 col-12">
                            <Typography variant="h5" component="h2">
                                {robot && robot.name ? robot.name : id}
                            </Typography>
                            {robot && robot.code ? (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    {robot.code}
                                </Typography>
                            ) : null}
                            {(loadError || reportPayload.apiError) && (
                                <Typography
                                    variant="body2"
                                    color="error"
                                    className="mt-2"
                                >
                                    <LocaleMessage msg="page.robots.cleaning_report.fetch_failed" />
                                    {reportPayload.apiError &&
                                    reportPayload.apiError.error
                                        ? ` — ${reportPayload.apiError.error}`
                                        : ''}
                                </Typography>
                            )}
                        </div>
                        <div className="col-md-4 col-12 text-md-right mt-2 mt-md-0">
                            <Tooltip
                                title={
                                    <LocaleMessage msg="page.robots.cleaning_report.force_full_sync_tooltip" />
                                }
                            >
                                <span className="d-inline-block mr-2">
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<MdSync />}
                                        onClick={fetchCleaningReportForceFull}
                                        disabled={reportLoading}
                                    >
                                        <LocaleMessage msg="page.robots.cleaning_report.force_full_sync" />
                                    </Button>
                                </span>
                            </Tooltip>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<MdSettings />}
                                onClick={goToRobotSettings}
                            >
                                <LocaleMessage msg="page.robots.cleaning_report.configure_robot" />
                            </Button>
                        </div>
                    </div>

                    <div className="col-12 mb-5 mt-3">
                        <CardSideBordered
                            title={
                                <LocaleMessage msg="page.robots.cleaning_report.title" />
                            }
                            Icon={MdCleaningServices}
                            hide
                        >
                            <div>
                                <ReportHeader
                                    date_props={{
                                        locale: date_loc,
                                    }}
                                    period={{
                                        minDate: null,
                                        fromDate: new Date(
                                            `${draftPeriod.startDate}T12:00:00`
                                        ),
                                        untilDate: new Date(
                                            `${draftPeriod.endDate}T12:00:00`
                                        ),
                                    }}
                                    handleFromDateChange={
                                        handleCleaningFromDateChange
                                    }
                                    handleUntilDateChange={
                                        handleCleaningUntilDateChange
                                    }
                                    reloadData={applyPeriod}
                                    shortcuts={{
                                        active: currShortcut,
                                        list: shortcut_list,
                                    }}
                                    handleShortcutClick={shortcutId =>
                                        handleCleaningPeriodShortcut(shortcutId)
                                    }
                                    exportButton={
                                        <ExportCSVButton
                                            label={
                                                <LocaleMessage msg="button.export_data" />
                                            }
                                            data={csvData}
                                            headers={csvHeaders}
                                            filename={GetFileName(
                                                `REPORT-cleaning-${(robot &&
                                                    robot.name) ||
                                                    id}`,
                                                'csv'
                                            )}
                                            disabled={csvData.length === 0}
                                        />
                                    }
                                />
                                <div className="sidecard-body mt-3">
                                    <div
                                        className="animated slideInUpTiny animation-duration-3 mb-5"
                                        style={{ width: '100%' }}
                                    >
                                        <div className="row card-row mb-5">
                                            <div className="col-md-3 col-sm-6 col-12 dash-card mb-3">
                                                <InfoCard
                                                    data={{
                                                        title: String(total),
                                                        subTitle: (
                                                            <LocaleMessage msg="page.robots.cleaning_report.card_total_records" />
                                                        ),
                                                    }}
                                                    color={cardColor}
                                                    icon="format-list-bulleted"
                                                    onClick={
                                                        scrollToCleaningTable
                                                    }
                                                />
                                            </div>
                                            <div className="col-md-3 col-sm-6 col-12 dash-card mb-3">
                                                <InfoCard
                                                    data={{
                                                        title: totalCleanFormatted,
                                                        subTitle: (
                                                            <LocaleMessage msg="page.robots.cleaning_report.card_clean_time" />
                                                        ),
                                                    }}
                                                    color={cardColor}
                                                    icon="timer"
                                                />
                                            </div>
                                            <div className="col-md-3 col-sm-6 col-12 dash-card mb-3">
                                                <InfoCard
                                                    data={{
                                                        title: `${totalArea} m²`,
                                                        subTitle: (
                                                            <LocaleMessage msg="page.robots.cleaning_report.card_clean_area" />
                                                        ),
                                                    }}
                                                    color={cardColor}
                                                    icon="border-style"
                                                />
                                            </div>
                                            <CleaningReportUtilityInfoCards
                                                consumption={consumption}
                                                cardColor={cardColor}
                                                robotDilutions={robotDilutions}
                                                list={list}
                                            />
                                        </div>

                                        <CleaningReportROICard
                                            totalArea={totalArea}
                                            cardColor={cardColor}
                                            chartColor={graphColor}
                                            list={list}
                                            humanProd={
                                                cleaningAppSettings.human_productivity
                                            }
                                            periodDates={periodDates}
                                        />

                                        <CleaningReportUtilityCharts
                                            list={list}
                                            periodDates={periodDates}
                                            localeCode={localeCode}
                                            consumption={consumption}
                                        />

                                        <CleaningReportBatteryDashboard
                                            list={list}
                                            localeCode={localeCode}
                                            periodDates={periodDates}
                                        />

                                        <div className="row card-row">
                                            <CleaningReportProductSpentCard
                                                list={list}
                                                localeCode={localeCode}
                                                consumption={consumption}
                                                robotDilutions={robotDilutions}
                                                periodDates={periodDates}
                                            />
                                            <CleaningReportBatteryByModeCard
                                                list={list}
                                                localeCode={localeCode}
                                            />
                                            {/* <CleaningReportFleetScheduleCard
                                                list={list}
                                                localeCode={localeCode}
                                            /> */}
                                        </div>

                                        <CleaningReportChartsCard
                                            cardColor={graphColor}
                                            chartColor={cardColor}
                                            pieSegmentColors={pieSegmentColors}
                                            list={list}
                                            localeCode={localeCode}
                                            periodDates={periodDates}
                                        />

                                        <CleaningReportAreaComparisonCard
                                            primaryColor={graphColor}
                                            secondaryColor={cardColor}
                                            data={areaCompareSeries}
                                        />

                                        <CleaningReportAreaByHourCard
                                            barColor={graphColor}
                                            data={areaByHourSeries}
                                        />

                                        <div className="row card-row">
                                            <div
                                                ref={cleaningTableRef}
                                                id="cleaning-report-table"
                                                className="col-12 mb-5 dash-card"
                                            >
                                                <div className="jr-card jr-card-widget p-3">
                                                    <Typography
                                                        variant="h6"
                                                        className="mb-3"
                                                    >
                                                        <LocaleMessage msg="page.robots.cleaning_report.table_title" />
                                                    </Typography>
                                                    <div className="table-responsive">
                                                        <table className="table table-striped mb-0">
                                                            <thead>
                                                                <tr>
                                                                    <th>
                                                                        <LocaleMessage msg="page.robots.cleaning_report.col_task" />
                                                                    </th>
                                                                    <th>
                                                                        <LocaleMessage msg="page.robots.cleaning_report.col_clean_time" />
                                                                    </th>
                                                                    <th>
                                                                        <LocaleMessage msg="page.robots.cleaning_report.col_area" />
                                                                    </th>
                                                                    <th>
                                                                        <LocaleMessage msg="page.robots.cleaning_report.col_created" />
                                                                    </th>
                                                                    <th>
                                                                        <LocaleMessage msg="page.robots.cleaning_report.col_mode" />
                                                                    </th>
                                                                    <th>
                                                                        <LocaleMessage msg="page.robots.cleaning_report.col_status" />
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {list.length ===
                                                                0 ? (
                                                                    <tr>
                                                                        <td
                                                                            colSpan={
                                                                                6
                                                                            }
                                                                            className="text-center text-muted"
                                                                        >
                                                                            <LocaleMessage msg="label.no_data" />
                                                                        </td>
                                                                    </tr>
                                                                ) : (
                                                                    tasksTableRows.map(
                                                                        (
                                                                            row,
                                                                            idx
                                                                        ) => (
                                                                            <tr
                                                                                key={
                                                                                    row.report_id !=
                                                                                    null
                                                                                        ? String(
                                                                                              row.report_id
                                                                                          )
                                                                                        : `row-${idx}`
                                                                                }
                                                                                onClick={() =>
                                                                                    openCleaningDetail(
                                                                                        row
                                                                                    )
                                                                                }
                                                                                style={{
                                                                                    cursor:
                                                                                        row.report_id !=
                                                                                            null &&
                                                                                        String(
                                                                                            row.report_id
                                                                                        ).trim() !==
                                                                                            ''
                                                                                            ? 'pointer'
                                                                                            : 'default',
                                                                                }}
                                                                            >
                                                                                <td>
                                                                                    {
                                                                                        row.task_name
                                                                                    }
                                                                                </td>
                                                                                <td>
                                                                                    {formatCleaningDuration(
                                                                                        row.clean_time
                                                                                    )}
                                                                                </td>
                                                                                <td>
                                                                                    {Number(
                                                                                        row.clean_area
                                                                                    ).toFixed(
                                                                                        2
                                                                                    )}
                                                                                </td>
                                                                                <td>
                                                                                    {formatCleaningReportCreateDateTime(
                                                                                        getCleaningReportTaskStartTime(
                                                                                            row
                                                                                        )
                                                                                    )}
                                                                                </td>
                                                                                <td>
                                                                                    {getCleaningModeLabel(
                                                                                        row.mode,
                                                                                        localeCode
                                                                                    )}
                                                                                </td>
                                                                                <td>
                                                                                    {getCleaningTaskStatusLabel(
                                                                                        row.status,
                                                                                        localeCode
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        )
                                                                    )
                                                                )}
                                                            </tbody>
                                                        </table>
                                                        <CleaningReportTasksTablePagination
                                                            count={list.length}
                                                            page={
                                                                tasksTablePage
                                                            }
                                                            rowsPerPage={
                                                                tasksTableRowsPerPage
                                                            }
                                                            onPageChange={
                                                                handleTasksTablePageChange
                                                            }
                                                            onRowsPerPageChange={
                                                                handleTasksTableRowsPerPageChange
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </CardSideBordered>
                    </div>

                    <CleaningReportDetailModal
                        open={detailModalOpen}
                        onClose={closeCleaningDetail}
                        loading={detailLoading}
                        errorMessage={detailError}
                        detail={detailData}
                        localeCode={localeCode}
                        chemicalDilutionRatio={
                            cleaningAppSettings.chemical_dilution_ratio
                        }
                    />
                </>
            </Container>
        </PageContent>
    );
}

export default injectIntl(CleaningReportsDashboard);
